import { type ComponentClass, createElement, type ReactNode } from 'react';
import type { ColumnDefTemplate } from '@tanstack/react-table';

function isClassComponent<TProps extends object>(
    template: (props: TProps) => unknown
): template is ComponentClass<TProps> & ((props: TProps) => unknown) {
    const prototype: unknown = template.prototype;
    return typeof prototype === 'object' && prototype !== null && 'isReactComponent' in prototype;
}

/**
 * Draws a column's cell, header or footer template. TanStack's `flexRender` mounts a function
 * template as a component of its own, and most tables build their columns on every render, so
 * every re-render (opening a dialog from a row, say) gave each cell a new component type and
 * React replaced its elements. Focus fell to the page, and a dialog had no opener left to hand
 * it back to. Calling the template inside this one component keeps the elements in place;
 * any hooks it calls belong to this component, one per cell, as they did to the old one.
 */
export function ColumnTemplate<TProps extends object>(props: {
    template: ColumnDefTemplate<TProps> | undefined;
    context: TProps;
}) {
    const { template, context } = props;
    if (template === undefined) return null;
    if (typeof template === 'string') return template;
    if (isClassComponent(template)) return createElement(template, context);
    const content: ReactNode = template(context);
    return content;
}
